import type { StudyStepSlug } from '@sigora/domain';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type Session,
  type SignedInUser,
  type StudySummary,
  createStudy,
  fetchSession,
  fetchStudies,
  logout,
} from './api/client.ts';
import { isApiError } from './api/errors.ts';
import { onUnauthorized } from './api/unauthorized.ts';
import { AccountView } from './auth/AccountView.tsx';
import { ForgotPasswordView } from './auth/ForgotPasswordView.tsx';
import { LoginView } from './auth/LoginView.tsx';
import { ResetPasswordView } from './auth/ResetPasswordView.tsx';
import { SignupView } from './auth/SignupView.tsx';
import { VerifyEmailView } from './auth/VerifyEmailView.tsx';
import { StudyList } from './studies/StudyList.tsx';
import { StudyShell } from './studies/StudyShell.tsx';
import { type Route, isSignedOutRoute, parseRoute, routeToPath } from './studies/routing.ts';
import type { HeaderAccount } from './ui/HeaderMenu.tsx';

export type LoadState =
  | { kind: 'loading' }
  /** `sessionLost` when a sign-in had just succeeded, which is a different thing to say. */
  | { kind: 'signedOut'; sessionLost?: boolean }
  | { kind: 'ready'; session: Session }
  | { kind: 'failed'; message: string };

/**
 * The session with a saved account applied, only while it is still that account's session. A save
 * answers whenever the server does, and by then the user may have signed out or another account
 * may have signed in; applying it regardless would put the old account back on screen.
 */
export const withSavedAccount = (state: LoadState, user: SignedInUser): LoadState =>
  state.kind === 'ready' && state.session.user.id === user.id
    ? { kind: 'ready', session: { user } }
    : state;

/**
 * Two screens and a history entry between them, so the router is a parsed pathname rather than a
 * routing library: there is nothing here that nested routes, loaders or code splitting would buy,
 * and a dependency with its own major-version cadence is not free.
 */
export const App = () => {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [route, setRoute] = useState<Route>(() => parseRoute(globalThis.location.pathname));
  /**
   * Bumped to read the session again — after signing in, and nowhere else. The session load is an
   * effect rather than a call, so a counter is what asks it to run a second time.
   */
  const [sessionEpoch, setSessionEpoch] = useState(0);
  /** Set when signing out failed, reported beside the control rather than in place of the app. */
  const [signOutError, setSignOutError] = useState<string | null>(null);
  /** Whether a sign-out is waiting on the server. */
  const [signingOut, setSigningOut] = useState(false);
  /**
   * Counts sign-ins in this tab. A sign-out answers for the session it was asked in, and one that
   * answers after another sign-in — the session ended under it and somebody signed in again — says
   * nothing about the session on screen, so it is dropped rather than signing that session out.
   */
  const signIns = useRef(0);
  const [studies, setStudies] = useState<StudySummary[] | null>(null);
  const [studiesError, setStudiesError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /**
   * Bumped to read the studies again while the list is already showing, which a delete is the
   * only thing that asks for: every other way the list changes arrives by navigating to it.
   */
  const [studiesEpoch, setStudiesEpoch] = useState(0);

  useEffect(() => {
    const onPopState = () => setRoute(parseRoute(globalThis.location.pathname));
    globalThis.addEventListener('popstate', onPopState);

    return () => globalThis.removeEventListener('popstate', onPopState);
  }, []);

  /**
   * A move the user asked for, behind the Back button. A move to the address already showing, such
   * as the wordmark clicked on the list it links to, adds no entry and leaves the address as it is,
   * query and fragment included: Back would only show the same screen again.
   */
  const go = useCallback((next: Route) => {
    const path = routeToPath(next);
    const { pathname, search, hash } = globalThis.location;
    if (path === pathname) {
      globalThis.history.replaceState(null, '', `${path}${search}${hash}`);
    } else {
      globalThis.history.pushState(null, '', path);
    }
    setRoute(next);
  }, []);

  /**
   * The same move without a history entry, for a URL the app is correcting rather than a place the
   * user asked to go: a study opened without a step, and a step the study has not reached. A push
   * there would put the address nobody wanted behind the Back button, where it would send them
   * straight back to the correction.
   */
  const replace = useCallback((next: Route) => {
    globalThis.history.replaceState(null, '', routeToPath(next));
    setRoute(next);
  }, []);

  /**
   * A session that ends while the app is open lands here, from whichever request found out. It is
   * registered before the first read so the read's own 401 cannot arrive with nowhere to go.
   *
   * `justSignedIn` is what makes the second visit to the login view say something. A 401 on the
   * read that follows a successful sign-in is not an expired session, it is a session that never
   * arrived — the shape a `Secure` cookie takes over plain http — and a blank form would be the
   * whole of the report.
   *
   * Cleared only by a session read that succeeded, because a 401 arrives twice for one failure: the
   * API layer reports it and the read's own rejection follows, and whichever ran second would
   * otherwise clear the flag the first had just set.
   */
  const justSignedIn = useRef(false);

  /** A sign-in succeeded: read the new session, with nothing of the last one's sign-out on it. */
  const signedIn = useCallback(() => {
    justSignedIn.current = true;
    signIns.current += 1;
    setSigningOut(false);
    setSignOutError(null);
    setState({ kind: 'loading' });
    setSessionEpoch((epoch) => epoch + 1);
  }, []);

  useEffect(
    () =>
      onUnauthorized(() => {
        setState({ kind: 'signedOut', sessionLost: justSignedIn.current });
        // The studies belong to whoever was signed in. Held across a sign-out they would be shown,
        // titles and all, to whoever signs in next — as a settled list rather than a stale one,
        // until that account's own read answered over the top of it.
        setStudies(null);
      }),
    [],
  );

  useEffect(() => {
    let active = true;

    void fetchSession().then(
      (session) => {
        if (!active) {
          return;
        }

        justSignedIn.current = false;
        setState({ kind: 'ready', session });
      },
      (error: unknown) => {
        if (!active) {
          return;
        }

        // A 401 is not a failure to report: it is the ordinary state of a browser that has not
        // signed in, and the login view is the answer to it.
        if (isApiError(error) && error.status === 401) {
          setState({ kind: 'signedOut', sessionLost: justSignedIn.current });

          return;
        }

        setState({
          kind: 'failed',
          message: error instanceof Error ? error.message : 'Sigora could not be reached.',
        });
      },
    );

    return () => {
      active = false;
    };
  }, [sessionEpoch]);

  /**
   * The list, read when the list screen is showing and not before.
   *
   * A study opened by its URL renders from the study itself, so a deep link never waits on this.
   * It is read again on every return to the list rather than cached, because a turn running in a
   * study can rename it or start another one while the list is off screen.
   */
  useEffect(() => {
    if (state.kind !== 'ready' || route.name !== 'studies') {
      return;
    }

    let active = true;
    setStudiesError(null);

    void fetchStudies().then(
      (rows) => {
        if (active) {
          setStudies(rows);
        }
      },
      (failure: unknown) => {
        if (active) {
          setStudies([]);
          setStudiesError(
            failure instanceof Error ? failure.message : 'Your studies could not be read.',
          );
        }
      },
    );

    return () => {
      active = false;
    };
  }, [state.kind, route.name, studiesEpoch]);

  /**
   * Before the gate: the screens for getting an account or back into one render by their path
   * alone, whatever the session is doing. A mailed link has to open its screen in a browser that is
   * signed in — perhaps as somebody else — as well as in one that is not, and none of them waits
   * on the session read.
   */
  if (isSignedOutRoute(route)) {
    switch (route.name) {
      case 'signup':
        return <SignupView onNavigate={go} />;
      case 'verify-email':
        return (
          <VerifyEmailView
            onNavigate={go}
            signedInAs={state.kind === 'ready' ? state.session.user.email : null}
            onSignOut={() =>
              logout().then(() => {
                setState({ kind: 'signedOut' });
                setStudies(null);
                go({ name: 'studies' });
              })
            }
          />
        );
      case 'forgot-password':
        return <ForgotPasswordView onNavigate={go} />;
      case 'reset-password':
        return (
          <ResetPasswordView
            onNavigate={go}
            onSignedIn={() => {
              // Whoever this tab showed before the reset — the same account or another — is gone.
              setStudies(null);
              signedIn();
              // Replaced rather than pushed: Back should not return to a link already spent.
              replace({ name: 'studies' });
            }}
          />
        );
    }
  }

  // Before everything else: with no session there is nothing to show.
  if (state.kind === 'signedOut') {
    return (
      <LoginView sessionLost={state.sessionLost ?? false} onSignedIn={signedIn} onNavigate={go} />
    );
  }

  if (state.kind === 'failed') {
    return (
      <p data-test-id="app-error" role="alert">
        {state.message}
      </p>
    );
  }

  if (state.kind === 'loading') {
    return <p data-test-id="app-loading">Loading…</p>;
  }

  /**
   * Who is signed in and the control that ends the session, handed to whichever screen is showing
   * so it sits in that screen's header menu.
   */
  const account: HeaderAccount = {
    email: state.session.user.email,
    signingOut,
    signOutError,
    onNavigate: go,
    onSignOut: () => {
      const askedIn = signIns.current;
      setSignOutError(null);
      setSigningOut(true);
      void logout().then(
        () => {
          if (signIns.current !== askedIn) {
            return;
          }
          setSigningOut(false);
          setState({ kind: 'signedOut' });
          // Cleared here as well as on a 401: signing out deliberately is the ordinary way one
          // account follows another in the same tab.
          setStudies(null);
        },
        /**
         * Signing out is a delete on the server, and a browser that could not reach it is not
         * signed out — showing the login view would be a lie about a session still live. So the
         * screen stays as it is and the failure is reported beside the control that failed, which
         * is also the control that retries it: replacing the app with the error would take that
         * control away.
         */
        () => {
          if (signIns.current !== askedIn) {
            return;
          }
          setSigningOut(false);
          setSignOutError('Signing out failed. Please try again.');
        },
      );
    },
  };

  if (route.name === 'account') {
    return (
      <AccountView
        user={state.session.user}
        onBack={() => go({ name: 'studies' })}
        onSaved={(user) => setState((current) => withSavedAccount(current, user))}
        account={account}
      />
    );
  }

  if (route.name === 'study' || route.name === 'study-step') {
    const openStep = (studyId: string, stepSlug: StudyStepSlug) =>
      go({ name: 'study-step', studyId, stepSlug });

    return (
      <StudyShell
        // Keyed by study and not by step, which is what makes every piece of per-study state in
        // the shell — an unsent draft, a restore offer, the conversation itself — belong to the
        // study it was made in while a move between that study's steps keeps all of it: switching
        // studies is a remount, walking through one is not.
        key={route.studyId}
        studyId={route.studyId}
        stepSlug={route.name === 'study-step' ? route.stepSlug : undefined}
        onBack={() => go({ name: 'studies' })}
        onOpenStep={openStep}
        onReplaceStep={(studyId, stepSlug) => replace({ name: 'study-step', studyId, stepSlug })}
        account={account}
      />
    );
  }

  return (
    <StudyList
      studies={studies ?? []}
      loading={studies === null}
      error={studiesError}
      creating={creating}
      onOpen={(studyId) => go({ name: 'study', studyId })}
      onDeleted={() => setStudiesEpoch((epoch) => epoch + 1)}
      onNew={() => {
        setCreating(true);
        setStudiesError(null);
        void createStudy().then(
          (studyId) => {
            setCreating(false);
            go({ name: 'study', studyId });
          },
          (failure: unknown) => {
            setCreating(false);
            setStudiesError(
              failure instanceof Error ? failure.message : 'The study could not be started.',
            );
          },
        );
      }}
      account={account}
    />
  );
};
